import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Clock, TrendingUp, AlertTriangle, CheckCircle2, Activity, Timer } from "lucide-react";
import HelpTip from "@/components/HelpTip";
import { MENU_HELP } from "@/lib/helpTexts";

function formatSeconds(s) {
    if (s === null || s === undefined) return "—";
    if (s < 60) return `${Math.round(s)}s`;
    if (s < 3600) return `${Math.round(s / 60)} min`;
    const h = Math.floor(s / 3600);
    const m = Math.round((s % 3600) / 60);
    return `${h}h ${m}m`;
}

const PERIODS = [
    { value: 7, label: "7 días" },
    { value: 30, label: "30 días" },
    { value: 90, label: "90 días" },
];

export default function SLA({ scope = "internal" }) {
    const [data, setData] = useState(null);
    const [days, setDays] = useState(30);

    useEffect(() => {
        (async () => {
            try {
                const url = scope === "portal" ? `/portal/sla?days=${days}` : `/dashboard/sla?days=${days}`;
                const { data } = await api.get(url);
                setData(data);
            } catch (e) {
                console.warn(e);
            }
        })();
    }, [days, scope]);

    if (!data) return <div className="text-sm text-slate-500 font-mono">Cargando métricas SLA...</div>;

    const compliance = data.compliance_pct;
    const complianceColor = compliance === null ? "text-slate-400" : compliance >= 90 ? "text-emerald-600" : compliance >= 75 ? "text-amber-600" : "text-red-600";

    return (
        <div className="space-y-4" data-testid="sla-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-display font-black text-2xl tracking-tight flex items-center gap-2">Cumplimiento SLA<HelpTip text={MENU_HELP.sla} variant="?" /></h2>
                    <div className="text-xs text-slate-500 font-mono">
                        Tiempos de respuesta y traslado · Últimos {data.period_days} días
                    </div>
                </div>
                <div className="flex items-center gap-1 fm-card p-1">
                    {PERIODS.map((p) => (
                        <button
                            key={p.value}
                            type="button"
                            onClick={() => setDays(p.value)}
                            data-testid={`sla-period-${p.value}`}
                            className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-sm transition-colors ${days === p.value ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* KPI Hero */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="sla-kpis">
                <div className="fm-card p-5 border-l-4 border-l-emerald-500">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Cumplimiento SLA</div>
                    <div className={`font-mono font-black text-4xl ${complianceColor}`}>
                        {compliance !== null ? `${Math.round(compliance)}%` : "—"}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                        {data.within_sla} cumplidos / {data.breached_sla} fuera
                    </div>
                </div>
                <div className="fm-card p-5">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1">
                        <Timer className="w-3 h-3" /> Tiempo de respuesta
                    </div>
                    <div className="font-mono font-black text-2xl mt-1">{formatSeconds(data.avg_response_seconds)}</div>
                    <div className="text-xs text-slate-500 mt-1">Recepción → arribo origen</div>
                </div>
                <div className="fm-card p-5">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1">
                        <Activity className="w-3 h-3" /> Tiempo de traslado
                    </div>
                    <div className="font-mono font-black text-2xl mt-1">{formatSeconds(data.avg_transport_seconds)}</div>
                    <div className="text-xs text-slate-500 mt-1">Paciente abordo → destino</div>
                </div>
                <div className="fm-card p-5">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> Tiempo total
                    </div>
                    <div className="font-mono font-black text-2xl mt-1">{formatSeconds(data.avg_total_seconds)}</div>
                    <div className="text-xs text-slate-500 mt-1">Recepción → finalizado</div>
                </div>
            </div>

            {/* Detail panel */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="sla-breakdown">
                <div className="fm-card p-4">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3 text-emerald-600" /> Dentro de SLA
                    </div>
                    <div className="font-mono font-bold text-3xl text-emerald-600">{data.within_sla}</div>
                </div>
                <div className="fm-card p-4">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3 text-red-600" /> Fuera de SLA
                    </div>
                    <div className="font-mono font-bold text-3xl text-red-600">{data.breached_sla}</div>
                </div>
                <div className="fm-card p-4">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Total servicios cerrados</div>
                    <div className="font-mono font-bold text-3xl">{data.total_services}</div>
                </div>
            </div>

            {data.no_data > 0 && (
                <div className="fm-card p-3 border-l-4 border-l-amber-500 bg-amber-50 text-xs text-slate-700 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                        <strong>{data.no_data}</strong> servicio(s) sin marcas GPS suficientes en su histórico — habilitando captura GPS en cada cambio de estatus (CrewMobile lo hace automáticamente) podrás medirlos.
                    </div>
                </div>
            )}

            {/* Table of recent services */}
            <div className="fm-card overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
                    <h3 className="font-display font-bold text-base flex items-center gap-2">
                        <TrendingUp className="w-4 h-4" /> Desglose por servicio
                    </h3>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 font-mono">
                        {data.services.length} más recientes
                    </div>
                </div>
                {data.services.length === 0 ? (
                    <div className="p-10 text-center text-sm text-slate-500">
                        Aún no hay servicios cerrados en este período.
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-200">
                                {["Folio", "Paciente", "Tipo", "Urgencia", "Asignación", "Respuesta", "Traslado", "Total", "SLA"].map((h) => (
                                    <th key={h} className="text-left py-2.5 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {data.services.map((s) => (
                                <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`sla-row-${s.folio}`}>
                                    <td className="py-2 px-3 font-mono text-xs">
                                        <Link to={scope === "portal" ? `/portal/services/${s.id}` : `/services/${s.id}`} className="font-bold hover:text-red-600">
                                            {s.folio}
                                        </Link>
                                    </td>
                                    <td className="py-2 px-3">{s.patient_name}</td>
                                    <td className="py-2 px-3 text-xs text-slate-500">{s.service_type_name}</td>
                                    <td className="py-2 px-3 text-xs uppercase font-bold">{s.urgency || "—"}</td>
                                    <td className="py-2 px-3 font-mono text-xs">{formatSeconds(s.assign_seconds)}</td>
                                    <td className="py-2 px-3 font-mono text-xs">{formatSeconds(s.response_seconds)}</td>
                                    <td className="py-2 px-3 font-mono text-xs">{formatSeconds(s.transport_seconds)}</td>
                                    <td className="py-2 px-3 font-mono text-xs">{formatSeconds(s.total_seconds)}</td>
                                    <td className="py-2 px-3">
                                        {s.within_sla === null
                                            ? <span className="fm-badge fm-badge-neutral">Sin dato</span>
                                            : s.within_sla
                                                ? <span className="fm-badge fm-badge-positive">Cumple</span>
                                                : <span className="fm-badge fm-badge-critical">Fuera</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400 font-mono text-center py-2">
                Target SLA respuesta: crítica 15min · alta 20min · normal 30min · baja 45min
            </div>
        </div>
    );
}
